import type { CafeContentDocument } from '@mds/shared/types/content';

import { cn } from '@mds/shared/lib/utils';

import { formatCents } from '@/lib/money';

/**
 * A café document drawn the way a guest reads it: copy, pictures, and the menu
 * in its saved order. Read-only — used for the draft preview and for looking
 * back at an old revision.
 *
 * It is the content, not the public page's exact styling: the public site owns
 * its own layout, so this shows what will be said rather than pretending to be
 * a pixel-perfect screenshot of it.
 */
export default function CafePreview({
  document: doc,
  urls,
}: {
  document: CafeContentDocument;
  urls: ReadonlyMap<string, string>;
}) {
  const hero = doc.heroImageFileId ? urls.get(doc.heroImageFileId) : undefined;

  return (
    <article className="flex flex-col gap-6 text-ink">
      {hero ? (
        <img src={hero} alt="" className="h-48 w-full rounded-2xl object-cover" />
      ) : (
        doc.heroImageFileId && <Missing className="h-48" />
      )}

      <header className="flex flex-col gap-2">
        {doc.page.eyebrow && (
          <span className="font-mono text-sm tracking-wider text-brand-text uppercase">
            {doc.page.eyebrow}
          </span>
        )}
        <h3 className="m-0 text-3xl leading-tight font-semibold tracking-tighter">
          {doc.page.title || 'Untitled'}
        </h3>
        {doc.page.summary && <p className="m-0 text-lg">{doc.page.summary}</p>}
      </header>

      {doc.page.description && (
        <p className="m-0 text-base whitespace-pre-line text-ink-secondary">
          {doc.page.description}
        </p>
      )}

      {doc.categories.length === 0 ? (
        <p className="m-0 text-base text-ink-muted">The menu is empty.</p>
      ) : (
        doc.categories.map((c) => (
          <section key={c.id} className="flex flex-col gap-3">
            <div>
              <h4 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
                {c.name}
              </h4>
              {c.description && (
                <p className="m-0 mt-2 text-base text-ink-muted">{c.description}</p>
              )}
            </div>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
              {c.items.map((i) => {
                const image = i.imageFileId ? urls.get(i.imageFileId) : undefined;
                return (
                  <li key={i.id} className={cn('flex gap-3', !i.isAvailable && 'text-ink-muted')}>
                    {image ? (
                      <img
                        src={image}
                        alt=""
                        className="size-14 shrink-0 rounded-lg border border-hairline object-cover"
                      />
                    ) : (
                      i.imageFileId && <Missing className="size-14 shrink-0" />
                    )}
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="text-base font-semibold">{i.name}</span>
                        <span className="font-mono text-md tabular-nums">
                          {formatCents(i.priceAmountMinor)}
                        </span>
                      </div>
                      {i.description && <span className="text-base">{i.description}</span>}
                      {!i.isAvailable && (
                        <span className="font-mono text-sm tracking-wide uppercase">
                          Not available
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}

      {doc.page.menuNote && (
        <p className="m-0 border-t border-hairline pt-3 text-base text-ink-muted">
          {doc.page.menuNote}
        </p>
      )}

      {doc.galleryFileIds.length > 0 && (
        <ul className="m-0 grid list-none grid-cols-3 gap-2 p-0">
          {doc.galleryFileIds.map((id) => {
            const url = urls.get(id);
            return (
              <li key={id}>
                {url ? (
                  <img src={url} alt="" className="aspect-card w-full rounded-lg object-cover" />
                ) : (
                  <Missing className="aspect-card w-full" />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </article>
  );
}

/** A picture the file service cannot draw right now — deleted, or not public. */
function Missing({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'flex items-center justify-center rounded-lg border border-dashed border-hairline bg-raised px-2 text-center font-mono text-xs text-ink-muted',
        className,
      )}
    >
      Picture unavailable
    </span>
  );
}
